/**
 * Bản nháp ô soạn chat (Thinh 19/10): PIC gõ dở trong chat tổng / chat xe / một chuỗi rồi đóng chuỗi
 * hay rời trang thì lúc mở lại vẫn còn chữ và các @nhắc — như Slack.
 *
 * Lưu trong localStorage của máy đang gõ, mỗi nơi gửi một khoá (mạch chính, từng chuỗi). Không lưu
 * máy chủ: nháp đổi theo từng phím, ghi lên DB là một luồng ghi liên tục cho thứ chỉ chính người gõ
 * cần; đổi máy thì mất nháp — chấp nhận được. Nháp cũ hơn DRAFT_TTL_MS tự bỏ; đăng xuất xoá hết
 * (máy dùng chung không lộ chữ của người trước).
 */

const PREFIX = "chat-draft:v1:"
/** Sự kiện trong CÙNG tab khi nháp đổi (`storage` chỉ bắn sang tab khác) — dòng "và 1 bản nháp" nghe để cập nhật. */
export const DRAFT_CHANGED_EVENT = "chat-draft-changed"
export const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000
/** Nháp quá dài thì thôi lưu — localStorage có trần ~5MB cho cả trang. */
const MAX_DRAFT_CHARS = 20_000

export interface StoredDraft {
  text: string
  /** Phần riêng của từng ô soạn (@nhắc, loại tin, tệp đã tải…) — ô soạn tự kiểm khi đọc lại. */
  extra: unknown
  at: number
}

/** Khoá của một nơi gửi, vd. `draftKey("pic", picId, "thread", rootId)`. Thiếu phần nào thì không có khoá. */
export function draftKey(...parts: (string | null | undefined)[]): string | null {
  if (parts.some((p) => !p)) return null
  return `${PREFIX}${parts.join(":")}`
}

/**
 * Khoá nháp theo nơi gửi — ô soạn ghi và dòng tóm tắt chuỗi đọc PHẢI cùng một khoá, nên chỉ dựng ở đây.
 * Chat tổng (PicComposer): mạch chính, một chuỗi trả lời, chuỗi "Chat xe" của một xe.
 */
export const picDraftKeys = (picId: string | null | undefined) => {
  const pic = picId || "me"
  return {
    main: draftKey("pic", pic, "main"),
    thread: (rootId: string | null | undefined) => draftKey("pic", pic, "thread", rootId),
    carChat: (carId: string | null | undefined) => draftKey("pic", pic, "car", carId),
  }
}

/** Chat riêng của xe (CarComposer): mạch chính hoặc một chuỗi. */
export const carDraftKey = (carId: string | null | undefined, rootId?: string | null) =>
  rootId ? draftKey("car", carId, "thread", rootId) : draftKey("car", carId, "main")

function notifyDraftChanged(): void {
  try {
    window.dispatchEvent(new Event(DRAFT_CHANGED_EVENT))
  } catch {
    // Không có window (máy chủ, test) thì không có ai để báo.
  }
}

function storage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage
  } catch {
    return null
  }
}

/** Đọc nháp còn hạn; nháp hỏng / hết hạn thì xoá luôn. */
export function readDraft(key: string | null, now = Date.now()): StoredDraft | null {
  const store = storage()
  if (!key || !store) return null
  try {
    const raw = store.getItem(key)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    const draft = parsed as Partial<StoredDraft> | null
    if (!draft || typeof draft.text !== "string" || typeof draft.at !== "number" || now - draft.at > DRAFT_TTL_MS) {
      store.removeItem(key)
      notifyDraftChanged()
      return null
    }
    return { text: draft.text, extra: draft.extra ?? null, at: draft.at }
  } catch {
    return null
  }
}

/** Ghi nháp; chữ trống (đã gửi, đã xoá hết) là xoá nháp. */
export function writeDraft(key: string | null, text: string, extra: unknown, now = Date.now()): void {
  const store = storage()
  if (!key || !store) return
  try {
    const had = store.getItem(key) !== null
    if (!text.trim() || text.length > MAX_DRAFT_CHARS) store.removeItem(key)
    else store.setItem(key, JSON.stringify({ text, extra, at: now } satisfies StoredDraft))
    // Chỉ báo khi CÓ / KHÔNG đổi — gõ thêm chữ vào nháp sẵn có không làm vẽ lại cả mạch chat.
    if (had !== (store.getItem(key) !== null)) notifyDraftChanged()
  } catch {
    // Chặn lưu (chế độ riêng tư, đầy bộ nhớ) thì chỉ mất nháp; ô soạn vẫn gõ / gửi bình thường.
  }
}

/** Nơi gửi này có nháp chưa gửi không — cho dòng tóm tắt chuỗi ("và 1 bản nháp"). */
export function hasDraft(key: string | null | undefined): boolean {
  const store = storage()
  if (!key || !store) return false
  try {
    return store.getItem(key) !== null
  } catch {
    return false
  }
}

function draftKeys(store: Storage): string[] {
  const keys: string[] = []
  for (let i = 0; i < store.length; i++) {
    const key = store.key(i)
    if (key?.startsWith(PREFIX)) keys.push(key)
  }
  return keys
}

/** Bỏ mọi nháp quá hạn — gọi một lần khi mở ô soạn để nháp của chuỗi không bao giờ mở lại không đọng mãi. */
export function pruneDrafts(now = Date.now()): void {
  const store = storage()
  if (!store) return
  for (const key of draftKeys(store)) readDraft(key, now)
}

/** Xoá mọi nháp — khi đăng xuất. */
export function clearAllDrafts(): void {
  const store = storage()
  if (!store) return
  try {
    for (const key of draftKeys(store)) store.removeItem(key)
  } catch {
    // như writeDraft
  }
  notifyDraftChanged()
}

/** Mảng các phần tử có đủ trường chuỗi — kiểm `extra` đọc lại từ máy (có thể là bản cũ / bị sửa tay). */
export function arrayOf<T>(value: unknown, fields: string[]): T[] {
  if (!Array.isArray(value)) return []
  return value.filter((item): item is T => !!item && typeof item === "object"
    && fields.every((f) => typeof (item as Record<string, unknown>)[f] === "string"))
}
